import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ResourcePage from "@/components/ResourcePage";
import InvestorPortal from "@/pages/InvestorPortal";
import AuditLog from "@/pages/AuditLog";
import Utenti from "@/pages/Utenti";
import OperationDetail from "@/pages/OperationDetail";
import Invoices from "@/pages/Invoices";
import PrimaNota from "@/pages/PrimaNota";
import Bank from "@/pages/Bank";
import Variance from "@/pages/Variance";
import EmailSettings from "@/pages/EmailSettings";
import UnitDetail from "@/pages/UnitDetail";
import Scadenzario from "@/pages/Scadenzario";
import Ritenute from "@/pages/Ritenute";
import Payments from "@/pages/Payments";
import Inbox from "@/pages/Inbox";
import Automations from "@/pages/Automations";
import Treasury from "@/pages/Treasury";
import Tasks from "@/pages/Tasks";
import Integrations from "@/pages/Integrations";
import Documents from "@/pages/Documents";
import Cashflow from "@/pages/Cashflow";
import SetPassword from "@/pages/SetPassword";
import Cantieri from "@/pages/Cantieri";
import AiMemoria from "@/pages/AiMemoria";
import Legal from "@/pages/Legal";
import Sentinella from "@/pages/Sentinella";
import Posta from "@/pages/Posta";
import Assistente from "@/pages/Assistente";
import Agenti from "@/pages/Agenti";
import RegistroAI from "@/pages/RegistroAI";
import Computer from "@/pages/Computer";
import Orders from "@/pages/Orders";
import Tickets from "@/pages/Tickets";
import Suppliers from "@/pages/Suppliers";
import InvestorDetail from "@/pages/InvestorDetail";
import Info from "@/pages/Info";
import SpecchiettoGC from "@/pages/SpecchiettoGC";
import Iva from "@/pages/Iva";

const Loading = () => <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento...</div>;

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "investitore") return <Navigate to="/portale" replace />;
  return <CompanyProvider><Layout>{children}</Layout></CompanyProvider>;
}

function InvestorProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/imposta-password" element={<SetPassword />} />
      <Route path="/info" element={<Info />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/termini" element={<Legal />} />
      <Route path="/portale" element={<InvestorProtected><InvestorPortal /></InvestorProtected>} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/societa" element={<Protected><ResourcePage resource="companies" /></Protected>} />
      <Route path="/operazioni" element={<Protected><ResourcePage resource="operations" /></Protected>} />
      <Route path="/investitori" element={<Protected><ResourcePage resource="investors" /></Protected>} />
      <Route path="/investitori/:id" element={<Protected><InvestorDetail /></Protected>} />
      <Route path="/investimenti" element={<Protected><ResourcePage resource="investments" /></Protected>} />
      <Route path="/conti" element={<Protected><ResourcePage resource="accounts" /></Protected>} />
      <Route path="/movimenti" element={<Protected><ResourcePage resource="movements" /></Protected>} />
      <Route path="/operazioni/:id" element={<Protected><OperationDetail /></Protected>} />
      <Route path="/fatture" element={<Protected><Invoices /></Protected>} />
      <Route path="/prima-nota" element={<Protected><PrimaNota /></Protected>} />
      <Route path="/banca" element={<Protected><Bank /></Protected>} />
      <Route path="/scostamenti" element={<Protected><Variance /></Protected>} />
      <Route path="/email" element={<Protected><EmailSettings /></Protected>} />
      <Route path="/unita/:id" element={<Protected><UnitDetail /></Protected>} />
      <Route path="/scadenzario" element={<Protected><Scadenzario /></Protected>} />
      <Route path="/ritenute" element={<Protected><Ritenute /></Protected>} />
      <Route path="/pagamenti" element={<Protected><Payments /></Protected>} />
      <Route path="/da-confermare" element={<Protected><Inbox /></Protected>} />
      <Route path="/automazioni" element={<Protected><Automations /></Protected>} />
      <Route path="/tesoreria" element={<Protected><Treasury /></Protected>} />
      <Route path="/specchietto-gc" element={<Protected><SpecchiettoGC /></Protected>} />
      <Route path="/iva" element={<Protected><Iva /></Protected>} />
      <Route path="/attivita" element={<Protected><Tasks /></Protected>} />
      <Route path="/integrazioni" element={<Protected><Integrations /></Protected>} />
      <Route path="/documenti" element={<Protected><Documents /></Protected>} />
      <Route path="/cantieri" element={<Protected><Cantieri /></Protected>} />
      <Route path="/fornitori" element={<Protected><Suppliers /></Protected>} />
      <Route path="/fornitori/preferiti" element={<Protected><Suppliers /></Protected>} />
      <Route path="/fornitori/scadenze" element={<Protected><Suppliers /></Protected>} />
      <Route path="/fornitori/report" element={<Protected><Suppliers /></Protected>} />
      <Route path="/ordini" element={<Protected><Orders /></Protected>} />
      <Route path="/assistenza" element={<Protected><Tickets /></Protected>} />
      <Route path="/previsione-cassa" element={<Protected><Cashflow /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
      <Route path="/utenti" element={<Protected><Utenti /></Protected>} />
      <Route path="/ai" element={<Protected><AiMemoria /></Protected>} />
      <Route path="/sentinella" element={<Protected><Sentinella /></Protected>} />
      <Route path="/posta" element={<Protected><Posta /></Protected>} />
      <Route path="/assistente" element={<Protected><Assistente /></Protected>} />
      <Route path="/agenti" element={<Protected><Agenti /></Protected>} />
      <Route path="/registro-ai" element={<Protected><RegistroAI /></Protected>} />
      <Route path="/computer" element={<Protected><Computer /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
